/**
 * Nový stôl — výber bojovníkov a arény (docs/design/4-vyber-bojovnikov.png).
 *
 * Rozloženie VS: vľavo tvoj hrdina, v strede súpiska, vpravo súper. Pri online
 * stole je súperova voľba **tajná** — vyberie si ju sám, keď príde cez odkaz.
 * V hot-seate a proti botovi sa vyberá za všetky miesta: klik na panel miesta
 * ho urobí aktívnym a súpiska potom vyberá preň.
 */

import type { HeroDef } from '@unmatched/engine';
import { content } from '@unmatched/content';
import { IMPLEMENTED_HEROES } from '@unmatched/engine';
import { useEffect, useState } from 'react';

import { ApiError, BOT_LEVEL_LABEL, matches, type BotLevel, type GameFormat, type MatchMode } from '../api.js';
import { DeckDialog } from '../ui/DeckDialog.js';
import { HeroPanel, SecretPanel } from '../ui/HeroPanel.js';
import { MapThumb } from '../ui/MapThumb.js';
import { Roster } from '../ui/Roster.js';

interface Props {
  nick: string;
  /** Režim predvolený z dlaždice v klubovni. */
  initialMode?: MatchMode;
  onOpenMatch: (id: string) => void;
  onCancel: () => void;
}

interface MapOption {
  id: string;
  name: string;
  spaces: number;
  zones?: number;
  starts?: number;
}

const MODE_LABEL: Record<MatchMode, string> = {
  online: 'Online',
  hotseat: 'Hot-seat',
  solo: 'Proti botovi',
};

const BOT_NOTES: Record<BotLevel, string> = {
  easy: 'Často zahrá niečo náhodné. Na učenie sa pravidiel.',
  medium: 'Hrá rozumne, ale s obranou a kartami nešetrí.',
  hard: 'Premýšľa dopredu a odhaduje tvoje karty z odhodu. Tvoju ruku nevidí.',
};

const MAP_NOTES: Record<string, string> = {
  'test-arena': 'Pravidelná mriežka — na učenie sa pravidiel.',
  klastor: 'Okruh okolo nádvoria, štyri priestory v dvoch zónach.',
  pristav: 'Hustý trh proti dlhému mólu k majáku.',
  marmoreal: 'Záhrada so sochami — veľa priestorov v dvoch aj troch zónach.',
  sarpedon: 'Ostrov a loď: pláž, ruiny a podpalubie.',
  'baskerville-manor': 'Sídlo a park. Tajné chodby (kľúčová dierka) — len na pohyb, nie na útok.',
  'fayrlund-forest': 'Les s potokom a pílou (The Witcher).',
  'streets-of-novigrad': 'Mestské ulice, kanál a prístav (The Witcher).',
};

export function NewTable({ nick, initialMode, onOpenMatch, onCancel }: Props) {
  const [heroes, setHeroes] = useState<HeroDef[]>([]);
  const [maps, setMaps] = useState<MapOption[]>([]);
  const [mode, setMode] = useState<MatchMode>(initialMode ?? 'online');
  const [botLevel, setBotLevel] = useState<BotLevel>('medium');
  /** Online: verejný stôl uvidí v klubovni každý a pridať sa smie ktokoľvek. */
  const [isPublic, setIsPublic] = useState(false);
  const [players, setPlayers] = useState(2);
  const [format, setFormat] = useState<GameFormat>('duel');
  const [picked, setPicked] = useState<(string | null)[]>([null, null, null, null]);
  const [activeSlot, setActiveSlot] = useState(0);
  const [mapId, setMapId] = useState<string | null>(null);
  const [deckHero, setDeckHero] = useState<HeroDef | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const options = await matches.options();
        setHeroes(options.heroes);
        setMaps(options.maps);
        setMapId(options.maps[0]?.id ?? null);
        // Predvolené sú len postavy, ktorými sa naozaj dá hrať.
        const playable = options.heroes.filter((hero) => IMPLEMENTED_HEROES.includes(hero.id));
        const first = playable[0]?.id ?? null;
        const second = playable[1]?.id ?? first;
        setPicked([first, second, first, second]);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Nepodarilo sa načítať ponuku');
      }
    })();
  }, []);

  /** Počet hráčov určuje, ktoré formáty aj ktoré mapy dávajú zmysel. */
  function setPlayerCount(count: number) {
    setPlayers(count);
    const current = maps.find((m) => m.id === mapId);
    if (current && (current.starts ?? 4) < count) {
      setMapId(maps.find((m) => (m.starts ?? 4) >= count)?.id ?? null);
    }
    setFormat(count === 2 ? 'duel' : format === 'teams' ? 'teams' : 'ffa');
    if (activeSlot >= count) setActiveSlot(0);
  }

  function pick(heroId: string) {
    setPicked((prev) => {
      const next = [...prev];
      next[activeSlot] = heroId;
      return next;
    });
    // V hot-seate po výbere preskočí na ďalšie miesto — ako pri draftovaní.
    if (mode !== 'online' && activeSlot < players - 1) setActiveSlot(activeSlot + 1);
  }

  function randomize() {
    const playable = heroes.filter((h) => IMPLEMENTED_HEROES.includes(h.id));
    if (!playable.length) return;
    const any = () => playable[Math.floor(Math.random() * playable.length)]!.id;
    setPicked((prev) => prev.map((id, slot) => (mode !== 'online' || slot === 0 ? any() : id)));
    const fitting = maps.filter((m) => (m.starts ?? 4) >= players);
    if (fitting.length) setMapId(fitting[Math.floor(Math.random() * fitting.length)]!.id);
  }

  async function start() {
    if (!mapId) return;
    const needed = mode === 'online' ? 1 : players;
    const chosen = picked.slice(0, needed);
    if (chosen.some((h) => !h)) return;
    setBusy(true);
    setError(null);
    try {
      // Online stôl si vyberá len tvoju postavu — ostatní si zvolia svoju sami.
      const match = await matches.create({
        mapId,
        heroes: chosen as string[],
        mode,
        players,
        format,
        botLevel: mode === 'solo' ? botLevel : undefined,
        public: mode === 'online' && isPublic,
      });
      onOpenMatch(match.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Stôl sa nepodarilo založiť');
      setBusy(false);
    }
  }

  const heroOf = (slot: number) => heroes.find((h) => h.id === picked[slot]) ?? null;
  const maxPlayers = Math.max(2, ...maps.map((m) => m.starts ?? 2));
  const pickedMap = maps.find((m) => m.id === mapId);
  const seatName = (slot: number) =>
    format === 'teams'
      ? `${slot + 1}P · tím ${slot % 2 === 0 ? 'A' : 'B'}`
      : mode !== 'hotseat' && slot === 0
        ? `1P · ${nick}`
        : mode === 'solo'
          ? `${slot + 1}P · bot`
          : `${slot + 1}P`;
  const others = Array.from({ length: players - 1 }, (_, i) => i + 1);
  const modeLabel =
    mode === 'online'
      ? `Online · ${isPublic ? 'verejný' : 'súkromný'} stôl`
      : mode === 'solo'
        ? `Proti botovi · ${BOT_LEVEL_LABEL[botLevel]}`
        : 'Hot-seat · jedna obrazovka';
  const formatLabel =
    format === 'teams' ? 'Tímy 2v2' : format === 'ffa' ? 'Každý za seba' : 'Duel 1v1';

  return (
    <div className="draft">
      <section className="panel draft__bar">
        <span className="tag tag--primary">Nový stôl</span>
        <h1 className="draft__title">
          {formatLabel} <i>//</i> Výber bojovníkov
        </h1>
        <div className="draft__controls">
          <div className="segment" role="group" aria-label="Kde sa hrá">
            {(['online', 'solo', 'hotseat'] as const).map((value) => (
              <button
                key={value}
                type="button"
                className={`segment__item ${mode === value ? 'is-active' : ''}`}
                onClick={() => {
                  setMode(value);
                  setActiveSlot(0);
                }}
              >
                {MODE_LABEL[value]}
              </button>
            ))}
          </div>
          {mode === 'online' ? (
            <div className="segment" role="group" aria-label="Kto sa smie pridať">
              <button
                type="button"
                className={`segment__item ${!isPublic ? 'is-active' : ''}`}
                title="Stôl nájde len ten, komu pošleš odkaz"
                onClick={() => setIsPublic(false)}
              >
                Súkromný
              </button>
              <button
                type="button"
                className={`segment__item ${isPublic ? 'is-active' : ''}`}
                title="Stôl uvidí v klubovni každý a pridať sa môže ktokoľvek"
                onClick={() => setIsPublic(true)}
              >
                Verejný
              </button>
            </div>
          ) : null}
          {mode === 'solo' ? (
            <div className="segment" role="group" aria-label="Obtiažnosť bota">
              {(['easy', 'medium', 'hard'] as const).map((level) => (
                <button
                  key={level}
                  type="button"
                  className={`segment__item ${botLevel === level ? 'is-active' : ''}`}
                  title={BOT_NOTES[level]}
                  onClick={() => setBotLevel(level)}
                >
                  {BOT_LEVEL_LABEL[level]}
                </button>
              ))}
            </div>
          ) : null}
          <div className="segment" role="group" aria-label="Počet hráčov">
            {[2, 3, 4].map((count) => (
              <button
                key={count}
                type="button"
                className={`segment__item ${players === count ? 'is-active' : ''}`}
                disabled={count > maxPlayers}
                onClick={() => setPlayerCount(count)}
              >
                {count} hráči
              </button>
            ))}
          </div>
          {players === 4 ? (
            <div className="segment" role="group" aria-label="Formát">
              <button
                type="button"
                className={`segment__item ${format === 'ffa' ? 'is-active' : ''}`}
                onClick={() => setFormat('ffa')}
              >
                Každý za seba
              </button>
              <button
                type="button"
                className={`segment__item ${format === 'teams' ? 'is-active' : ''}`}
                onClick={() => setFormat('teams')}
              >
                Tímy 2v2
              </button>
            </div>
          ) : null}
        </div>
      </section>

      <div className="draft__stage">
        <HeroPanel
          hero={heroOf(0)}
          slot={0}
          seat={seatName(0)}
          status={heroOf(0) ? <span className="tag tag--ok">● Pripravený</span> : null}
          active={activeSlot === 0}
          onClick={mode !== 'online' ? () => setActiveSlot(0) : undefined}
          onShowDeck={setDeckHero}
        />

        <div className="draft__center">
          <div className="vsBadge">
            <b>VS</b>
            <span>{mode === 'online' ? 'Tajný výber' : mode === 'solo' ? 'Proti botovi' : 'Draft'}</span>
          </div>
          <Roster
            heroes={heroes}
            picks={picked.slice(0, mode === 'online' ? 1 : players)}
            activeSlot={activeSlot}
            onPick={pick}
            onInspect={setDeckHero}
          />
          <p className="draft__hint">
            {mode === 'hotseat'
              ? `Vyberáš za ${activeSlot + 1}P — klikni na panel miesta, ak chceš vybrať za iné.`
              : mode === 'solo'
                ? activeSlot === 0
                  ? 'Vyberáš za seba. Klikni na panel bota, ak mu chceš vybrať postavu.'
                  : `Vyberáš postavu pre bota ${activeSlot + 1}P. ${BOT_NOTES[botLevel]}`
                : 'Vyberáš za seba. Súper si postavu zvolí sám po otvorení odkazu.'}
          </p>
        </div>

        <div className={`draft__others ${others.length > 1 ? 'is-many' : ''}`}>
          {others.map((slot) =>
            mode === 'online' ? (
              <SecretPanel key={slot} slot={slot} seat={seatName(slot)} title="Tajný výber hrdinu">
                <span className="secret__note">
                  Súper si postavu vyberie, keď otvorí tvoj odkaz. Voľbu uvidíš až pri vstupe
                  do arény.
                </span>
              </SecretPanel>
            ) : (
              <HeroPanel
                key={slot}
                hero={heroOf(slot)}
                slot={slot}
                seat={seatName(slot)}
                active={activeSlot === slot}
                compact={others.length > 1}
                status={
                  activeSlot === slot ? (
                    <span className="tag tag--primary">Vyberá sa</span>
                  ) : mode === 'solo' ? (
                    <span className="tag">Bot · {BOT_LEVEL_LABEL[botLevel]}</span>
                  ) : null
                }
                onClick={() => setActiveSlot(slot)}
                onShowDeck={setDeckHero}
              />
            ),
          )}
        </div>
      </div>

      <section className="panel">
        <header className="panel__head">
          <h2 className="panel__title">
            <span className="tag tag--primary">Stage select</span> Výber bojovej arény
          </h2>
          <span className="panel__meta">
            Farba priestoru je zóna — útok na diaľku zasiahne kohokoľvek v rovnakej farbe.
          </span>
        </header>
        <div className="stages">
          {maps.map((map) => {
            const full = content.maps[map.id];
            const tooSmall = (map.starts ?? 4) < players;
            return (
              <button
                key={map.id}
                type="button"
                className={`stageCard ${mapId === map.id ? 'is-picked' : ''}`}
                disabled={tooSmall}
                onClick={() => setMapId(map.id)}
              >
                <span className="stageCard__thumb">
                  {full ? <MapThumb map={full} width={260} /> : null}
                  {mapId === map.id ? <span className="stageCard__picked">✓ Vybraná</span> : null}
                  {map.zones ? <span className="stageCard__zones">{map.zones} zóny</span> : null}
                </span>
                <span className="stageCard__name">
                  {map.name}
                  <span>{map.spaces} priestorov</span>
                </span>
                <span className="stageCard__note">
                  {tooSmall
                    ? `Len ${map.starts} štarty — pre ${players} hráčov primalá.`
                    : (MAP_NOTES[map.id] ?? `Až ${map.starts ?? '?'} hráči.`)}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {error ? <p className="formError">{error}</p> : null}

      <section className="panel draft__launch">
        <div className="draft__summary">
          <span className="tag">{modeLabel}</span>
          <span className="tag">{formatLabel}</span>
          <span className="tag">{pickedMap?.name ?? 'bez arény'}</span>
          <span className="draft__matchup">
            {(mode === 'online' ? [picked[0] ?? null] : picked.slice(0, players))
              .map((id) => heroes.find((h) => h.id === id)?.name ?? '?')
              .join(' vs ')}
            {mode === 'online' ? ' vs ?' : ''}
          </span>
        </div>
        <div className="draft__actions">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Späť
          </button>
          <button type="button" className="btn" onClick={randomize} disabled={!heroes.length}>
            ⚄ Náhodný výber
          </button>
          <button
            type="button"
            className="btn btn--primary btn--xl"
            onClick={start}
            disabled={busy || !mapId}
          >
            {busy ? 'Zakladám…' : mode === 'online' ? '⚔ Vytvoriť stôl' : '⚔ Fight! Do arény'}
          </button>
        </div>
      </section>

      {deckHero ? <DeckDialog hero={deckHero} onClose={() => setDeckHero(null)} /> : null}
    </div>
  );
}
